import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import portalApi from '../../../../lib/portalApi';
import CreateCalendarItemModal from '../components/CreateCalendarItemModal';
import {
  Building2,
  FileText,
  Calendar,
  CheckCircle2,
  Plus,
  Search,
  ExternalLink,
  Download,
  AlertCircle,
  Clock,
  ArrowLeft,
  ChevronRight,
  ShieldCheck,
} from 'lucide-react';

export default function MentorStartupDetailPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('documents'); // 'documents' | 'meetings' | 'tasks'
  const [docSearch, setDocSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [holdModalItem, setHoldModalItem] = useState(null);
  const [holdReason, setHoldReason] = useState('');

  const loadStartupDetail = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await portalApi.getMentorStartup(id);
      setData(res);
    } catch (err) {
      console.error('Failed to load mentor startup detail:', err);
      setError(err.message || 'Failed to load startup details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStartupDetail();
  }, [id]);

  const handleStatusChange = async (task, newStatus, reason = '') => {
    try {
      await portalApi.patchMentorCalendarItem(task.id, {
        type: 'Task',
        status: newStatus,
        statusReason: reason,
      });
      loadStartupDetail();
    } catch (err) {
      alert(err.message || 'Failed to update task status');
    }
  };

  const handleCancelMeeting = async (meetingId) => {
    if (!window.confirm('Cancel this scheduled meeting?')) return;
    try {
      await portalApi.deleteMentorCalendarItem(meetingId, 'Meeting');
      loadStartupDetail();
    } catch (err) {
      alert(err.message || 'Failed to cancel meeting');
    }
  };

  if (loading) {
    return <div className="p-12 text-center text-xs text-[#8E8E99]">Loading startup details…</div>;
  }

  if (error || !data?.startup) {
    return (
      <div className="p-8 text-center space-y-4">
        <AlertCircle className="w-8 h-8 text-red-400 mx-auto" />
        <div className="text-sm font-medium text-[#F2F2F4]">{error || 'Startup not found'}</div>
        <Link
          to="/mentor-dashboard/startups"
          className="inline-flex items-center gap-1.5 text-xs text-[#D42B92] hover:underline"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to My Startups
        </Link>
      </div>
    );
  }

  const { startup, documents = [], meetings = [], tasks = [], deadlines = [] } = data;

  const filteredDocs = documents.filter((d) =>
    (d.title || d.originalName || '').toLowerCase().includes(docSearch.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Back Link */}
      <Link
        to="/mentor-dashboard/startups"
        className="inline-flex items-center gap-1.5 text-xs font-medium text-[#8E8E99] hover:text-[#F2F2F4] transition-colors"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        <span>Back to Assigned Startups</span>
      </Link>

      {/* Hobnob Header matching Reference D & PDF Page 5 */}
      <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl p-6 shadow-xl">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            {startup.logo ? (
              <img
                src={startup.logo}
                alt=""
                className="w-16 h-16 rounded-2xl object-cover border border-[#2C2C34]"
              />
            ) : (
              <div className="w-16 h-16 rounded-2xl bg-[#321129] border border-[#D42B92]/30 flex items-center justify-center text-xl font-bold text-[#D42B92]">
                {startup.name.charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <div className="flex items-center gap-2.5">
                <h1 className="text-2xl sm:text-3xl font-extrabold text-[#F2F2F4] tracking-tight">
                  {startup.name}
                </h1>
                <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                  {startup.status}
                </span>
              </div>
              <div className="text-xs text-[#8E8E99] mt-1 flex flex-wrap items-center gap-2">
                <span className="font-medium text-[#F2F2F4]">{startup.founderName}</span>
                <span>·</span>
                <span>{startup.founderEmail}</span>
                {startup.batchName && (
                  <>
                    <span>·</span>
                    <span className="text-purple-400 font-medium">{startup.batchName}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons matching PDF Page 5 */}
          <div className="flex flex-wrap items-center gap-2.5">
            <button
              onClick={() => setActiveTab('documents')}
              className={`px-4 py-2 rounded-xl text-xs font-semibold border transition-all ${
                activeTab === 'documents'
                  ? 'bg-[#321129] border-[#D42B92]/50 text-[#F2F2F4]'
                  : 'bg-[#08080A] border-[#2C2C34] text-[#8E8E99] hover:text-[#F2F2F4]'
              }`}
            >
              All Documents ({documents.length})
            </button>
            <button
              onClick={() => setModalOpen(true)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#08080A] border border-[#2C2C34] hover:border-[#D42B92]/40 text-[#F2F2F4] text-xs font-semibold transition-colors"
            >
              <Plus className="w-3.5 h-3.5 text-[#D42B92]" />
              <span>Task</span>
            </button>
            <button
              onClick={() => setModalOpen(true)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#D42B92] hover:bg-[#b02279] text-white text-xs font-semibold transition-colors shadow-lg shadow-[#D42B92]/20"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Meeting</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tabs / Sub-panels */}
      <div className="flex items-center gap-2 border-b border-[#2C2C34] pb-1">
        {[
          { id: 'documents', label: 'All Documents', count: documents.length, icon: FileText },
          { id: 'meetings', label: 'Meetings', count: meetings.length, icon: Calendar },
          { id: 'tasks', label: `Tasks for ${startup.name}`, count: tasks.length + deadlines.length, icon: CheckCircle2 },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-xs font-semibold transition-all ${
              activeTab === t.id
                ? 'bg-[#131318] text-[#D42B92] border-t border-x border-[#2C2C34]'
                : 'text-[#8E8E99] hover:text-[#F2F2F4]'
            }`}
          >
            <t.icon className="w-3.5 h-3.5" />
            <span>{t.label}</span>
            <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-white/[0.05]">
              {t.count}
            </span>
          </button>
        ))}
      </div>

      {/* ─── SECTION A: DOCUMENTS (Explicitly Shared Only) ───────────────── */}
      {activeTab === 'documents' && (
        <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl p-5 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-xs text-[#8E8E99]">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
              <span>Protected repository: Showing explicitly shared documents assigned to you.</span>
            </div>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8E99]" />
              <input
                type="text"
                value={docSearch}
                onChange={(e) => setDocSearch(e.target.value)}
                placeholder="Search documents..."
                className="pl-8 pr-3 py-1.5 rounded-xl bg-[#08080A] border border-[#2C2C34] text-xs text-[#F2F2F4] placeholder-[#8E8E99] focus:outline-none focus:border-[#D42B92] w-48 sm:w-64"
              />
            </div>
          </div>

          {filteredDocs.length === 0 ? (
            <div className="py-12 text-center text-xs text-[#8E8E99]">
              {docSearch
                ? 'No documents matching search.'
                : 'No documents shared with you yet for this startup. Documents must be explicitly shared by an administrator.'}
            </div>
          ) : (
            <div className="divide-y divide-[#2C2C34]/40">
              {filteredDocs.map((doc) => (
                <div
                  key={doc.id}
                  className="py-3 px-2 flex items-center justify-between gap-4 hover:bg-white/[0.02] rounded-xl transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="p-2 rounded-lg bg-white/[0.04] text-[#D42B92]">
                      <FileText className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-semibold text-[#F2F2F4] truncate">{doc.title}</div>
                      <div className="text-[11px] text-[#8E8E99]">
                        Shared on {new Date(doc.sharedDate).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <a
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] text-xs font-medium text-[#F2F2F4] transition-colors"
                    >
                      <Download className="w-3.5 h-3.5 text-[#D42B92]" />
                      <span>Download</span>
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─── SECTION B: MEETINGS ─────────────────────────────────────────── */}
      {activeTab === 'meetings' && (
        <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold text-[#F2F2F4]">Upcoming & Past Meetings</h2>
            <button
              onClick={() => setModalOpen(true)}
              className="flex items-center gap-1 text-xs font-semibold text-[#D42B92] hover:underline"
            >
              <Plus className="w-3.5 h-3.5" /> Schedule Meeting
            </button>
          </div>

          {meetings.length === 0 ? (
            <div className="py-12 text-center text-xs text-[#8E8E99]">
              No meetings scheduled for this startup.
            </div>
          ) : (
            <div className="space-y-3">
              {meetings.map((m) => {
                const dateObj = new Date(m.date);
                const day = dateObj.getDate();
                const month = dateObj.toLocaleString('en-US', { month: 'short' }).toUpperCase();
                const time = dateObj.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

                return (
                  <div
                    key={m.id}
                    className="p-4 rounded-xl bg-[#08080A] border border-[#2C2C34] flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                  >
                    <div className="flex items-center gap-4">
                      {/* Date Badge matching PDF Page 5 */}
                      <div className="w-12 h-12 rounded-xl bg-[#131318] border border-[#2C2C34] flex flex-col items-center justify-center text-center shrink-0">
                        <span className="text-base font-extrabold text-[#F2F2F4] leading-none">{day}</span>
                        <span className="text-[10px] font-bold text-[#D42B92] leading-none mt-0.5">{month}</span>
                      </div>
                      <div>
                        <div className="text-sm font-bold text-[#F2F2F4]">{m.title}</div>
                        <div className="text-xs text-[#8E8E99] flex items-center gap-2 mt-0.5">
                          <span>{time}</span>
                          {m.durationHours && <span>· {m.durationHours}h</span>}
                          {m.meetingLink && <span className="text-emerald-400">· meeting link attached</span>}
                          {m.status === 'Cancelled' && (
                            <span className="text-red-400 font-semibold">· CANCELLED</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 self-end sm:self-center">
                      {m.meetingLink && m.status !== 'Cancelled' && (
                        <a
                          href={m.meetingLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-4 py-1.5 rounded-full bg-[#D42B92] hover:bg-[#b02279] text-white text-xs font-semibold transition-colors flex items-center gap-1 shadow-sm"
                        >
                          <span>Join</span>
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                      {m.isCreator && m.status !== 'Cancelled' && (
                        <button
                          onClick={() => handleCancelMeeting(m.id)}
                          className="px-3 py-1.5 rounded-full border border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs font-medium transition-colors"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ─── SECTION C: TASKS & DEADLINES ─────────────────────────────────── */}
      {activeTab === 'tasks' && (
        <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold text-[#F2F2F4]">Tasks & Milestones</h2>
            <button
              onClick={() => setModalOpen(true)}
              className="flex items-center gap-1 text-xs font-semibold text-[#D42B92] hover:underline"
            >
              <Plus className="w-3.5 h-3.5" /> Add Task
            </button>
          </div>

          {tasks.length === 0 && deadlines.length === 0 ? (
            <div className="py-12 text-center text-xs text-[#8E8E99]">
              No tasks or deadlines created for this startup yet.
            </div>
          ) : (
            <div className="space-y-3">
              {/* Deadlines Section */}
              {deadlines.length > 0 && (
                <div className="space-y-2 mb-4">
                  <div className="text-[11px] font-bold text-[#8E8E99] uppercase tracking-wider">
                    Milestone Deadlines (All-day)
                  </div>
                  {deadlines.map((dl) => (
                    <div
                      key={dl.id}
                      className="p-3.5 rounded-xl bg-[#08080A] border border-[#2C2C34] flex items-center justify-between gap-3"
                    >
                      <div>
                        <div className="text-xs font-bold text-[#F2F2F4]">{dl.title}</div>
                        <div className="text-[11px] text-[#8E8E99] mt-0.5">
                          Milestone date: {new Date(dl.dueDate).toLocaleDateString()}
                        </div>
                      </div>
                      <span className="text-[10px] px-2 py-0.5 rounded-md bg-purple-500/10 text-purple-400 font-semibold border border-purple-500/20">
                        Deadline
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Startup Tasks List with 5 Status Workflow */}
              <div className="space-y-2">
                <div className="text-[11px] font-bold text-[#8E8E99] uppercase tracking-wider">
                  Startup Tasks ({tasks.length})
                </div>
                {tasks.map((t) => (
                  <div
                    key={t.id}
                    className="p-3.5 rounded-xl bg-[#08080A] border border-[#2C2C34] flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                  >
                    <div>
                      <div className="text-xs font-bold text-[#F2F2F4]">{t.title}</div>
                      <div className="text-[11px] text-[#8E8E99] mt-0.5 flex items-center gap-2">
                        <span>Due {new Date(t.dueDate).toLocaleDateString()}</span>
                        <span>·</span>
                        <span className="text-amber-400">Priority: {t.priority}</span>
                        {t.statusReason && (
                          <>
                            <span>·</span>
                            <span className="text-amber-300 italic font-medium">"{t.statusReason}"</span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Status Dropdown / Actions matching PDF Page 5 */}
                    <div className="flex items-center gap-2">
                      <select
                        value={t.status}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === 'On Hold') {
                            setHoldModalItem(t);
                          } else {
                            handleStatusChange(t, val);
                          }
                        }}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border focus:outline-none ${
                          t.status === 'Done'
                            ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40'
                            : t.status === 'In Review'
                            ? 'bg-purple-500/20 text-purple-300 border-purple-500/40'
                            : t.status === 'In Progress'
                            ? 'bg-sky-500/20 text-sky-300 border-sky-500/40'
                            : t.status === 'On Hold'
                            ? 'bg-amber-500/20 text-amber-300 border-amber-500/40'
                            : 'bg-white/5 text-[#8E8E99] border-white/10'
                        }`}
                      >
                        <option value="To Do">To Do</option>
                        <option value="In Progress">In Progress</option>
                        <option value="On Hold">On Hold</option>
                        <option value="In Review">In Review</option>
                        <option value="Done">Done</option>
                      </select>

                      {t.status === 'In Review' && (
                        <button
                          onClick={() => handleStatusChange(t, 'Done')}
                          className="px-2.5 py-1 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 text-xs font-semibold border border-emerald-500/30 transition-colors"
                        >
                          Approve Done
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* On Hold Reason Modal */}
      {holdModalItem && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl w-full max-w-md p-5 space-y-4 shadow-2xl">
            <h3 className="text-sm font-bold text-[#F2F2F4]">Reason for On Hold</h3>
            <p className="text-xs text-[#8E8E99]">
              Please provide a clear reason why "{holdModalItem.title}" is paused or blocked.
            </p>
            <textarea
              value={holdReason}
              onChange={(e) => setHoldReason(e.target.value)}
              required
              rows={3}
              placeholder="e.g. Waiting on client data / admin confirmation..."
              className="w-full px-3.5 py-2 rounded-xl bg-[#08080A] border border-[#2C2C34] text-xs text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
            />
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                onClick={() => {
                  setHoldModalItem(null);
                  setHoldReason('');
                }}
                className="px-3 py-1.5 rounded-xl border border-[#2C2C34] text-xs text-[#8E8E99]"
              >
                Cancel
              </button>
              <button
                disabled={!holdReason.trim()}
                onClick={() => {
                  handleStatusChange(holdModalItem, 'On Hold', holdReason.trim());
                  setHoldModalItem(null);
                  setHoldReason('');
                }}
                className="px-4 py-1.5 rounded-xl bg-[#D42B92] hover:bg-[#b02279] text-xs text-white font-medium disabled:opacity-50"
              >
                Confirm On Hold
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      <CreateCalendarItemModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={loadStartupDetail}
        preselectedStartupId={startup.id}
        assignedStartups={[{ id: startup.id, name: startup.name }]}
      />
    </div>
  );
}
